import axios from 'axios';
import { getMimicUser } from './mimic';

const host = process.env.REACT_APP_BACKEND_URL;

const axiosInstance = axios.create({
  baseURL: host,
});

// Pick the most useful text the backend gave us. Most endpoints answer with
// `{ message }`, but the access guards (e.g. the unpaid-invoice leads block)
// answer with `{ error }`.
const extractMessage = (response) =>
  response.data?.message ||
  response.data?.error ||
  response.statusText ||
  `Request failed with status ${response.status}`;

axiosInstance.interceptors.request.use((config) => {
  if (config.skipMimic) return config;
  const mimic = getMimicUser();
  if (mimic && mimic.id && mimic.type) {
    config.headers['X-Mimic-User-Id'] = mimic.id;
    config.headers['X-Mimic-User-Type'] = mimic.type;
  }
  return config;
});

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    const { response } = error;

    if (response && response.status === 403) {
      console.warn('Permission denied:', response.data?.message || 'Access denied');
    }

    if (!response) {
      return Promise.reject(error);
    }

    // This interceptor used to reject with the raw AxiosResponse. A response
    // object has no `message`, so every caller doing `err.message` (the auth
    // screens among them) fell back to a generic "Something went wrong" and the
    // real reason — "Customer is inactive", "No such email" — never reached the
    // user. Reject with a real Error carrying the backend message, while
    // keeping `data` / `status` / `response` reachable so the many callers
    // written against the old shape keep working.
    const normalizedError = new Error(extractMessage(response));
    normalizedError.data = response.data;
    normalizedError.status = response.status;
    normalizedError.statusText = response.statusText;
    normalizedError.headers = response.headers;
    normalizedError.config = response.config;
    normalizedError.response = response;

    return Promise.reject(normalizedError);
  },
);

export default axiosInstance;
